import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payment/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="noise-overlay" />
      
      <div className="max-w-md w-full text-center">
        <Link to="/" className="font-heading text-2xl font-bold text-[#C9A84C] inline-block mb-8">
          SAC CITY SHINE
        </Link>

        {status === 'checking' && (
          <div className="bg-surface border border-[#262626] p-8" data-testid="payment-checking">
            <Loader2 className="h-16 w-16 animate-spin text-white mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Processing Payment...
            </h1>
            <p className="text-gray-400">
              Please wait while we confirm your payment.
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-surface border border-[#262626] p-8" data-testid="payment-success">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Payment Successful!
            </h1>
            <p className="text-gray-400 mb-6">
              Your $100 deposit has been received and your appointment is now confirmed. 
              You'll receive a confirmation email shortly.
            </p>
            <div className="bg-surface-highlight border border-[#262626] p-4 mb-6">
              <p className="text-white font-medium">What's Next?</p>
              <p className="text-gray-400 text-sm mt-2">
                We'll arrive at your location on the scheduled date and time. 
                The remaining balance will be collected after the service.
              </p>
            </div>
            <Link to="/">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm">
                Return to Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-surface border border-[#262626] p-8" data-testid="payment-pending">
            <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Payment Processing
            </h1>
            <p className="text-gray-400 mb-6">
              Your payment is still being processed. 
              You'll receive a confirmation email once it's complete.
            </p>
            <Link to="/">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm">
                Return to Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-surface border border-[#262626] p-8" data-testid="payment-error">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl text-white">!</span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Something Went Wrong
            </h1>
            <p className="text-gray-400 mb-6">
              We couldn't verify your payment. 
              Please contact us if you believe this is an error.
            </p>
            <Link to="/">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm">
                Return to Home
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
